/**
 * Striking a side match, and watching it from the Score tab.
 *
 * Both live where the golf is. A Nassau is agreed on the first tee, in the
 * ninety seconds between pulling a driver and hitting it, and it is read walking
 * off a green — so it is one line under the card, not a screen anybody has to go
 * looking for.
 *
 * Which means: no button here may ever block on the network. Creating and
 * voiding go through the outbox for the same reason signing for a card does.
 */

import { useMemo, useState } from 'react'
import {
  autoStrokesFor,
  eighteens,
  type PressMode,
  type SideMatch as SideMatchRow,
} from '../engine/sideMatch'
import type { Tournament } from '../engine/types'
import { playerName } from '../select'
import { createSideMatch, mergedSideMatches, useStore } from '../store'
import { SectionRule } from '../ui'
import { dollars, standingLabel, thruLabel, useSideStanding } from './SideBoard'

/**
 * One line per match: `Springer  2 UP thru 4  $20`.
 *
 * Its own component rather than a row rendered in a loop, because each line
 * derives a whole Nassau and that derivation is a hook. Twelve one-line
 * components is also how a corrected score repairs one line and re-renders
 * nothing else.
 */
function StripRow({
  doc,
  match,
  me,
  onOpen,
}: {
  doc: Tournament
  match: SideMatchRow
  me: string
  onOpen: (matchId: string) => void
}) {
  const standing = useSideStanding(doc, match)
  if (!standing) return null

  const { walk, settlement } = standing
  const nameA = playerName(doc, match.playerA)
  const nameB = playerName(doc, match.playerB)
  const total = walk.standings.find((s) => s.bet.id === 'total')

  // From the reader's own point of view when he is in it: the opponent is the
  // name worth printing, since he knows perfectly well who he is.
  const mine = match.playerA === me || match.playerB === me
  const heading = mine
    ? playerName(doc, match.playerA === me ? match.playerB : match.playerA)
    : `${nameA} v ${nameB}`

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(match.id)}
        className="w-full text-left px-4 py-2.5 flex items-center gap-3 bc-tap"
      >
        <span className="bc-name text-sm flex-1 min-w-0 truncate">
          {heading}
          {match.voidedAt && <span className="text-mute text-[12px] ml-1.5">called off</span>}
        </span>
        <span className="text-right">
          <span className="bc-num text-base block leading-none">
            {match.voidedAt
              ? '—'
              : total
                ? standingLabel(total.up, nameA, nameB)
                : 'no holes'}
          </span>
          <span className="text-[11px] text-mute font-semibold uppercase tracking-wider">
            {match.voidedAt
              ? 'void'
              : `${thruLabel(walk)} · ${dollars(match.amountCents)}${
                  settlement.provisional ? ' so far' : ''
                }`}
          </span>
        </span>
      </button>
    </li>
  )
}

/**
 * The strip, and the way in to striking one.
 *
 * Only matches that cover the round on screen. A man scoring Tuesday's front
 * does not need Sunday's Nassau in front of him, and the board is one tap away
 * for the ones he does.
 */
export function SideMatchStrip({
  doc,
  roundId,
  onOpen,
}: {
  doc: Tournament
  roundId: string
  onOpen: (matchId: string) => void
}) {
  const matches = useStore(mergedSideMatches)
  const me = useStore((s) => s.session?.playerId ?? '')
  const [setup, setSetup] = useState(false)

  const here = matches
    .filter((m) => m.roundIds.includes(roundId))
    // A man's own matches first; then, within each, the newest.
    .sort((a, b) => {
      const mine = (m: SideMatchRow) => (m.playerA === me || m.playerB === me ? 0 : 1)
      return mine(a) - mine(b) || b.createdAt.localeCompare(a.createdAt)
    })

  const canStrike = eighteens(doc).some((e) => e.roundIds.includes(roundId))

  return (
    <>
      {here.length > 0 && (
        <>
          <SectionRule>Side matches</SectionRule>
          <ul className="divide-y divide-rule border-b border-rule">
            {here.map((m) => (
              <StripRow key={m.id} doc={doc} match={m} me={me} onOpen={onOpen} />
            ))}
          </ul>
        </>
      )}

      {setup ? (
        <SideMatchSetup
          doc={doc}
          roundId={roundId}
          onDone={(matchId) => {
            setSetup(false)
            if (matchId) onOpen(matchId)
          }}
        />
      ) : (
        canStrike && (
          <div className="px-4 py-3 border-b border-rule">
            <button
              type="button"
              onClick={() => setSetup(true)}
              className="bc-tap bc-label border-2 border-ink px-4 py-2.5 w-full"
            >
              {here.length > 0 ? 'Another side match' : 'Side match'}
            </button>
          </div>
        )
      )}
    </>
  )
}

/**
 * The deal: who, over which eighteen, how many strokes, how much, and presses.
 *
 * `offered` is not on the list. It needs the trailing man to answer a question
 * from his own phone, and answering it is only fair when both men are in the same
 * foursome — otherwise consecutive tee times mean one of them always knows what
 * he has already shot before he accepts. That is stage three; until then a match
 * is presses everywhere or presses nowhere, which are both symmetric.
 */
export function SideMatchSetup({
  doc,
  roundId,
  onDone,
}: {
  doc: Tournament
  roundId: string
  onDone: (matchId: string | null) => void
}) {
  const me = useStore((s) => s.session?.playerId ?? '')
  const live = useStore(mergedSideMatches)

  const options = useMemo(() => eighteens(doc), [doc])
  const [roundIds, setRoundIds] = useState<string[]>(
    () => options.find((e) => e.roundIds.includes(roundId))?.roundIds ?? options[0]?.roundIds ?? [],
  )
  /*
   * Nobody, until he says who.
   *
   * This defaulted to the first man in the roster who was not you — not your
   * foursome, not alphabetical, not even filtered on `active`. So the sheet
   * opened with a complete, strikeable $20 automatic-press bet against Tyler
   * already loaded, and "Shake on it" enabled: one tap struck a live wager
   * against a man who is not in your group, in a mode the sheet's own copy warns
   * can compound to $240.
   *
   * The button already refuses an empty opponent, so an empty default is the
   * whole fix — it turns an accident into a decision.
   */
  const [opponent, setOpponent] = useState('')
  const [strokeMode, setStrokeMode] = useState<'auto' | 'manual'>('auto')
  const [giver, setGiver] = useState<'me' | 'them'>('me')
  const [strokeCount, setStrokeCount] = useState(0)
  const [amount, setAmount] = useState(20)
  const [pressMode, setPressMode] = useState<PressMode>('auto')

  const auto = useMemo(
    () => autoStrokesFor(doc, me, opponent, roundIds),
    [doc, me, opponent, roundIds],
  )
  // Positive is always "A gives B", and A is whoever is holding the phone.
  const strokes = strokeMode === 'auto' ? auto : giver === 'me' ? strokeCount : -strokeCount

  const already = live.find(
    (m) =>
      !m.voidedAt &&
      ((m.playerA === me && m.playerB === opponent) ||
        (m.playerB === me && m.playerA === opponent)) &&
      m.roundIds.length === roundIds.length &&
      m.roundIds.every((r) => roundIds.includes(r)),
  )

  const strike = () => {
    const match = createSideMatch({
      roundIds,
      playerA: me,
      playerB: opponent,
      strokes,
      // The one conversion in the feature. The table is cents and the engine is
      // dollars, and every figure renders a hundred times too large if this
      // happens anywhere else.
      amountCents: Math.round(amount * 100),
      pressMode,
      ...(already ? { allowDuplicate: true } : {}),
    })
    onDone(match.id)
  }

  const opponentName = playerName(doc, opponent)

  return (
    <div className="border-b border-rule bg-wash">
      <div className="grid grid-cols-2 gap-2 px-4 py-3">
        <label className="grid gap-1">
          <span className="bc-label text-mute">Against</span>
          <select
            value={opponent}
            onChange={(e) => setOpponent(e.target.value)}
            className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
          >
            {/*
              An empty first option, because the sheet no longer picks for you —
              and the men you are actually walking with first, since a side match
              is usually struck on a tee with somebody standing next to you.
            */}
            <option value="">Pick a man…</option>
            {[...doc.players.filter((p) => p.id !== me && p.active)]
              .sort((a, b) => {
                const mine = doc.groups.find(
                  (g) => roundIds.includes(g.roundId) && g.playerIds.includes(me),
                )
                const near = (id: string) => (mine?.playerIds.includes(id) ? 0 : 1)
                return near(a.id) - near(b.id) || a.displayName.localeCompare(b.displayName)
              })
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
          </select>
        </label>
        <label className="grid gap-1">
          <span className="bc-label text-mute">Each bet</span>
          <input
            type="number"
            min={1}
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
            className="border-2 border-ink px-3 py-2 bc-num bc-tap"
          />
        </label>
      </div>

      <label className="grid gap-1 px-4 pb-3">
        <span className="bc-label text-mute">Over</span>
        <select
          value={roundIds.join('|')}
          onChange={(e) => setRoundIds(e.target.value.split('|'))}
          className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
        >
          {options.map((o) => (
            <option key={o.roundIds.join('|')} value={o.roundIds.join('|')}>
              {o.label}
            </option>
          ))}
        </select>
        {/* Named rather than implied, because "why can I not pick that nine" is
            otherwise a question with no answer on screen. */}
        <span className="text-[13px] text-mute leading-snug">
          A Nassau is front, back and total, so it is exactly eighteen — one round, or
          two nines on the same course and day.
        </span>
      </label>

      <div className="grid grid-cols-2 border-y border-rule">
        {(['auto', 'manual'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setStrokeMode(m)}
            className={`bc-label py-2.5 bc-tap ${
              strokeMode === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
            }`}
          >
            {m === 'auto' ? 'From handicaps' : 'Agreed on the tee'}
          </button>
        ))}
      </div>

      {strokeMode === 'auto' ? (
        <p className="px-4 py-3 bc-name text-sm">
          {auto === 0
            ? 'Level — no strokes either way.'
            : `${auto > 0 ? 'You give' : `${opponentName} gives you`} ${Math.abs(auto)}.`}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 px-4 py-3">
          <label className="grid gap-1">
            <span className="bc-label text-mute">Given by</span>
            <select
              value={giver}
              onChange={(e) => setGiver(e.target.value as 'me' | 'them')}
              className="border-2 border-ink px-2 py-2.5 bc-name text-sm bc-tap bg-paper"
            >
              <option value="me">You</option>
              <option value="them">{opponentName}</option>
            </select>
          </label>
          <label className="grid gap-1">
            <span className="bc-label text-mute">Strokes</span>
            <input
              type="number"
              min={0}
              max={18}
              value={strokeCount}
              onChange={(e) => setStrokeCount(Math.max(0, Number(e.target.value)))}
              className="border-2 border-ink px-2 py-2 bc-num bc-tap"
            />
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 border-y border-rule">
        {(['auto', 'none'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setPressMode(m)}
            className={`bc-label py-2.5 bc-tap ${
              pressMode === m ? 'bg-ink text-paper' : 'bg-paper text-mute'
            }`}
          >
            {m === 'auto' ? 'Automatic presses' : 'No presses'}
          </button>
        ))}
      </div>
      <p className="px-4 py-3 text-[13px] text-mute leading-snug">
        {pressMode === 'auto'
          ? 'A press goes on wherever one of you is two down, and a press can itself be pressed. That is where the money compounds — a lopsided eighteen at $20 a bet can reach $240.'
          : 'Front, back and total. Three bets, whatever happens.'}{' '}
        Both the strokes and this choice are frozen once the match is live — changing
        the rules of a bet halfway round is not a feature.
      </p>

      {already && (
        <p className="px-4 pb-3 text-[13px] text-signal leading-snug">
          You and {opponentName} already have a match on those holes. Striking this one
          makes it a second bet, not a correction.
        </p>
      )}

      <div className="flex gap-2 px-4 pb-4">
        <button
          type="button"
          onClick={() => onDone(null)}
          className="bc-tap flex-1 border-2 border-ink bc-label py-3"
        >
          Never mind
        </button>
        <button
          type="button"
          disabled={!opponent || roundIds.length === 0 || amount <= 0}
          onClick={strike}
          className="bc-tap flex-1 bg-signal text-paper bc-label py-3 disabled:opacity-30"
        >
          {already ? 'Strike a second' : 'Shake on it'}
        </button>
      </div>
    </div>
  )
}
